// cspell:disable
import { NetSuiteExchangeReturnValueApplicationZodSchema } from "@redotech/redo-return-core-common/integrations/returns/netsuite/netsuite";
import type * as zod from "zod";
import {
  array,
  object,
  optional,
  string,
  union,
  unknown,
  enum as zenum,
} from "zod";

const netSuiteBodyFieldDefaultValueSchema = unknown().refine(
  (value) => value != null && value !== "",
  "Default value is required",
);

export const netSuiteRequiredBodyFieldSchema = object({
  fieldId: string(),
  defaultValue: netSuiteBodyFieldDefaultValueSchema,
});

export const netSuiteRequiredBodyFieldSchemaWithValidation = object({
  fieldId: string().min(1, "Field ID is required"),
  defaultValue: netSuiteBodyFieldDefaultValueSchema,
});

export const netsuiteNewOrderSettingsSchema = object({
  discountItemId: string().optional(),
  returnValueApplication:
    NetSuiteExchangeReturnValueApplicationZodSchema.optional(),
  customerDepositAccountId: string().optional(),
  customerDepositPaymentMethodId: string().optional(),
  requiredBodyFields: array(netSuiteRequiredBodyFieldSchema),
});

export const netsuiteNewOrderSettingsSchemaWithValidation = object({
  discountItemId: string()
    .regex(/^\d*$/, "Discount item ID must be a number")
    .optional(),
  returnValueApplication:
    NetSuiteExchangeReturnValueApplicationZodSchema.optional(),
  customerDepositAccountId: string()
    .regex(/^\d*$/, "Customer deposit account ID must be a number")
    .optional(),
  customerDepositPaymentMethodId: string()
    .regex(/^\d*$/, "Customer deposit payment method ID must be a number")
    .optional(),
  requiredBodyFields: array(
    netSuiteRequiredBodyFieldSchemaWithValidation,
  ).optional(),
});

export const fieldValueTypeSchema = zenum(["string", "number", "boolean"]);
export type FieldValueType = zod.infer<typeof fieldValueTypeSchema>;

export const netSuiteCustomFieldValueMappingSchema = object({
  sourceValue: string(),
  targetValue: string(),
});
export type NetSuiteCustomFieldValueMapping = zod.infer<
  typeof netSuiteCustomFieldValueMappingSchema
>;

// Level 3 (deepest): only string
const fieldValueLevel3Schema = string();

// Level 3 entry: key with string value
const fieldEntryLevel3Schema_ = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel3Schema,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(array(netSuiteCustomFieldValueMappingSchema)),
});
export const fieldEntryLevel3Schema: zod.ZodType<FieldEntryLevel3> =
  fieldEntryLevel3Schema_;
export interface FieldEntryLevel3 extends zod.infer<
  typeof fieldEntryLevel3Schema_
> {}

// Level 2: string or array of level 3 entries
const fieldValueLevel2Schema_ = union([
  string(),
  array(fieldEntryLevel3Schema),
]);
export const fieldValueLevel2Schema: zod.ZodType<FieldValueLevel2> =
  fieldValueLevel2Schema_;
export type FieldValueLevel2 = string | FieldEntryLevel3[];

// Level 2 entry: key with level 2 value
const fieldEntryLevel2Schema_ = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel2Schema,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(array(netSuiteCustomFieldValueMappingSchema)),
});
export const fieldEntryLevel2Schema: zod.ZodType<FieldEntryLevel2> =
  fieldEntryLevel2Schema_;
export interface FieldEntryLevel2 extends zod.infer<
  typeof fieldEntryLevel2Schema_
> {}

// Level 1 (top level): string or array of level 2 entries
const fieldValueLevel1Schema_ = union([
  string(),
  array(fieldEntryLevel2Schema),
]);
export const fieldValueLevel1Schema: zod.ZodType<FieldValueLevel1> =
  fieldValueLevel1Schema_;
export type FieldValueLevel1 = string | FieldEntryLevel2[];

// Top-level schema for custom document fields (used on RMAs, credit memos, etc.)
export const netSuiteCustomDocumentFieldSchema = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel1Schema,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(array(netSuiteCustomFieldValueMappingSchema)),
});

// Top-level schema for custom document fields on line items
export const netSuiteCustomDocumentFieldItemSchema = object({
  fieldKey: string(),
  fieldValue: fieldValueLevel1Schema,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(array(netSuiteCustomFieldValueMappingSchema)),
});

// Validation versions with min length checks
const netSuiteCustomFieldValueMappingSchemaWithValidation = object({
  sourceValue: string().trim().min(1, "Source value is required"),
  targetValue: string().trim().min(1, "NetSuite value is required"),
});

const fieldEntryLevel3SchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: string().min(1, "Field value is required"),
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(
    array(netSuiteCustomFieldValueMappingSchemaWithValidation),
  ),
});

const fieldValueLevel2SchemaWithValidation: zod.ZodType<FieldValueLevel2> =
  union([
    string().min(1, "Field value is required"),
    array(fieldEntryLevel3SchemaWithValidation),
  ]);

const fieldEntryLevel2SchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel2SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(
    array(netSuiteCustomFieldValueMappingSchemaWithValidation),
  ),
});

const fieldValueLevel1SchemaWithValidation: zod.ZodType<FieldValueLevel1> =
  union([
    string().min(1, "Field value is required"),
    array(fieldEntryLevel2SchemaWithValidation),
  ]);

export const netSuiteCustomDocumentFieldSchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel1SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(
    array(netSuiteCustomFieldValueMappingSchemaWithValidation),
  ),
});

export const netSuiteCustomDocumentFieldItemSchemaWithValidation = object({
  fieldKey: string().min(1, "Field key is required"),
  fieldValue: fieldValueLevel1SchemaWithValidation,
  valueType: optional(fieldValueTypeSchema),
  valueMappings: optional(
    array(netSuiteCustomFieldValueMappingSchemaWithValidation),
  ),
});

export const netSuiteCustomLineItemOnRMASchema = object({
  itemId: string(),
  locationId: string(),
  rateTemplate: string(),
  amountTemplate: string(),
});

export const netSuiteCustomLineItemOnRMASchemaWithValidation = object({
  itemId: string().min(1, "Item ID is required"),
  locationId: string().min(1, "Location ID is required"),
  rateTemplate: string().min(1, "Rate template is required"),
  amountTemplate: string().min(1, "Amount template is required"),
});

export const netsuiteProcessingCustomFieldSchema = object({
  fieldName: string().min(1, "Field name is required"),
  values: array(string()),
});

export const netsuiteRMACustomizationSchema = object({
  customFieldsOnRMA: array(netSuiteCustomDocumentFieldSchema).optional(),
  netSuiteCustomLineItemsOnRMAS: array(
    netSuiteCustomLineItemOnRMASchema,
  ).optional(),
  customFieldsOnLineItem: array(
    netSuiteCustomDocumentFieldItemSchema,
  ).optional(),
});

export const netsuiteRMACustomizationSchemaWithValidation = object({
  customFieldsOnRMA: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
  netSuiteCustomLineItemsOnRMAS: array(
    netSuiteCustomLineItemOnRMASchemaWithValidation,
  ).optional(),
  customFieldsOnLineItem: array(
    netSuiteCustomDocumentFieldItemSchemaWithValidation,
  ).optional(),
});

export const netsuiteCreditMemoCustomizationSchema = object({
  customFieldsOnCreditMemo: array(netSuiteCustomDocumentFieldSchema).optional(),
});

export const netsuiteCreditMemoCustomizationSchemaWithValidation = object({
  customFieldsOnCreditMemo: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
});

export const netsuiteCashRefundCustomizationSchema = object({
  customFieldsOnCashRefund: array(netSuiteCustomDocumentFieldSchema).optional(),
});

export const netsuiteCashRefundCustomizationSchemaWithValidation = object({
  customFieldsOnCashRefund: array(
    netSuiteCustomDocumentFieldSchemaWithValidation,
  ).optional(),
});
